import { DateTime } from 'luxon';

/**
 * Configuration Constants
 */
export const FLOW_DATATYPE = 'air-flow';
export const POWER_DATATYPE = 'power';

// Pair tag prefix for identification
export const PAIR_TAG_PREFIX = 'PAIR:';

/**
 * Unit Pairing & Filtering Functions
 */

/**
 * Checks if a unit has a specific dataType in its RecordedDataTypes
 * @param {Object} unit - Unit object
 * @param {string} dataType - DataType to check for
 * @returns {boolean}
 */
const unitHasDataType = (unit, dataType) => {
  if (!unit.RecordedDataTypes || !Array.isArray(unit.RecordedDataTypes))
    return false;
  return unit.RecordedDataTypes.some(
    (recordedType) =>
      recordedType.DataTypes && recordedType.DataTypes.includes(dataType),
  );
};

/**
 * Finds unit pairs by matching tags with PAIR: prefix
 * @param {Array} units - Array of unit objects with Tags property
 * @returns {Array} Array of { pairTag, flowUnit, powerUnit }
 */
export const findUnitPairs = (units) => {
  if (!units || !Array.isArray(units)) return [];

  // Extract all pair tags from all units
  const pairTagsMap = new Map();

  units.forEach((unit) => {
    if (!unit.Tags || !Array.isArray(unit.Tags)) return;

    unit.Tags.forEach((tag) => {
      if (tag.Text && tag.Text.startsWith(PAIR_TAG_PREFIX)) {
        const pairTag = tag.Text.substring(PAIR_TAG_PREFIX.length);

        if (!pairTagsMap.has(pairTag)) {
          pairTagsMap.set(pairTag, { flowUnit: null, powerUnit: null });
        }

        const pair = pairTagsMap.get(pairTag);

        // Assign to flow or power based on unit's RecordedDataTypes
        if (unitHasDataType(unit, FLOW_DATATYPE)) {
          pair.flowUnit = unit;
        } else if (unitHasDataType(unit, POWER_DATATYPE)) {
          pair.powerUnit = unit;
        }
      }
    });
  });

  // Convert map to array, filter out incomplete pairs
  const pairs = [];
  pairTagsMap.forEach((pair, pairTag) => {
    if (pair.flowUnit && pair.powerUnit) {
      pairs.push({
        pairTag,
        flowUnit: pair.flowUnit,
        powerUnit: pair.powerUnit,
      });
    }
  });

  return pairs;
};

/**
 * Filters units by specific dataType (checks RecordedDataTypes structure)
 * @param {Array} units - Array of unit objects
 * @param {string} dataType - DataType to filter by
 * @returns {Array} Filtered unit array
 */
export const filterUnitsByDataType = (units, dataType) => {
  if (!units || !Array.isArray(units)) return [];
  return units.filter((unit) => {
    if (!unit.RecordedDataTypes || !Array.isArray(unit.RecordedDataTypes))
      return false;
    return unit.RecordedDataTypes.some(
      (recordedType) =>
        recordedType.DataTypes && recordedType.DataTypes.includes(dataType),
    );
  });
};

/**
 * Returns only units that are part of a pair
 * @param {Array} units - Array of unit objects
 * @param {Array} pairs - Array of pair objects from findUnitPairs
 * @returns {Array} Filtered unit array
 */
export const getPairedUnitsOnly = (units, pairs) => {
  if (!units || !Array.isArray(units) || !pairs || !Array.isArray(pairs))
    return [];

  const pairedUnitIds = new Set();
  pairs.forEach((pair) => {
    if (pair.flowUnit) pairedUnitIds.add(pair.flowUnit.UnitId);
    if (pair.powerUnit) pairedUnitIds.add(pair.powerUnit.UnitId);
  });

  return units.filter((unit) => pairedUnitIds.has(unit.UnitId));
};

/**
 * Data Calculation Functions
 */

/**
 * Calculates total air volume from meter summaries
 * @param {Array} meterSummaries - Array of meter summary objects
 * @param {Array<number> | null} [selectedUnitIds] - Optional array of selected unit IDs
 * @returns {number} Total volume in m³
 */
export const calculateTotalAirVolume = (meterSummaries, selectedUnitIds) => {
  if (!meterSummaries || !Array.isArray(meterSummaries)) return 0;

  let summaries = [];

  // Filter by selected units if specified
  if (selectedUnitIds && Array.isArray(selectedUnitIds)) {
    summaries = meterSummaries.filter((ms) =>
      selectedUnitIds.includes(ms.UnitId),
    );
  }

  return summaries.reduce((acc, ms) => acc + (ms.TotalVolume || 0), 0);
};

/**
 * Calculates total energy from meter summaries
 * @param {Array} meterSummaries - Array of meter summary objects
 * @param {Array<number> | null} [selectedUnitIds] - Optional array of selected unit IDs
 * @returns {number} Total energy in kWh
 */
export const calculateTotalEnergy = (meterSummaries, selectedUnitIds) => {
  if (!meterSummaries || !Array.isArray(meterSummaries)) return 0;

  let summaries = [];

  // Filter by selected units if specified
  if (selectedUnitIds && Array.isArray(selectedUnitIds)) {
    summaries = meterSummaries.filter((ms) =>
      selectedUnitIds.includes(ms.UnitId),
    );
  }

  return summaries.reduce((acc, ms) => acc + (ms.TotalEnergy || 0), 0);
};

/**
 * Calculates efficiency percentage
 * TODO: Get exact formula from domain expert
 * PLACEHOLDER: This is a simplified calculation
 * @param {number} totalVolume - Total air volume in L
 * @param {number} totalEnergy - Total energy in kWh
 * @returns {number} Efficiency percentage
 */
export const calculateEfficiency = (totalVolume, totalEnergy) => {
  // Constants for isothermal compression (matches Power BI DAX formula)
  const R = 8.314; // Universal gas constant (J/(mol·K))
  const T = 298.15; // Inlet temperature: 25°C in Kelvin
  const P1 = 1.0; // Inlet pressure in bar (atmospheric)
  const P2 = 6.0 + 1.0; // Outlet pressure in bar absolute (6 bar gauge + 1 bar atmospheric)
  const P1_Pa = P1 * 100_000; // Convert to Pascal

  if (!totalVolume || totalVolume === 0 || !totalEnergy || totalEnergy === 0) {
    return 0;
  }

  const volumeM3 = totalVolume;

  // Moles of air per m³ at inlet conditions (ideal gas: n = P·V / R·T, V = 1 m³)
  const molesPerM3 = P1_Pa / (R * T);

  // Isothermal energy per m³ (kWh/m³): n·R·T·ln(P2/P1) / 3,600,000
  // Simplifies to P1_Pa·ln(P2/P1) / 3,600,000 since n·R·T = P1_Pa
  const isothermalEnergyPerM3 =
    (molesPerM3 * R * T * Math.log(P2 / P1)) / 3_600_000;

  // Actual energy per m³ (kWh/m³)
  const actualEnergyPerM3 = totalEnergy / volumeM3;

  return (isothermalEnergyPerM3 / actualEnergyPerM3) * 100;
};

/**
 * Calculates specific energy consumption
 * @param {number} totalEnergy - Total energy in kWh
 * @param {number} totalVolume - Total air volume in m³
 * @returns {number} Specific energy in kWh/m³
 */
export const calculateSpecificEnergy = (totalEnergy, totalVolume) => {
  if (!totalVolume || totalVolume === 0) return 0;
  return totalEnergy / totalVolume;
};

/**
 * Calculates totals using only timestamps where both flow and power exist.
 * This prevents KPI skew when one series has gaps.
 * @param {Array} seriesData - Array of points with volume/energy and hasFlow/hasPower
 * @returns {{ totalVolume: number, totalEnergy: number }}
 */
export const calculateMatchedTotals = (seriesData) => {
  if (!seriesData || !Array.isArray(seriesData)) {
    return { totalVolume: 0, totalEnergy: 0 };
  }

  return seriesData.reduce(
    (acc, point) => {
      if (point?.hasFlow && point?.hasPower) {
        acc.totalVolume += point.volume || 0;
        acc.totalEnergy += point.energy || 0;
      }
      return acc;
    },
    { totalVolume: 0, totalEnergy: 0 },
  );
};

/**
 * Calculates horizontal band values from visible chart data
 * @param {Array} visibleData - Array of data points in visible range
 * @returns {Array} Array of { value, label, color }
 */
export const calculateAverageBands = (visibleData) => {
  if (!visibleData || !Array.isArray(visibleData) || visibleData.length === 0)
    return [];

  // Calculate average volume (m³)
  const avgVolume =
    visibleData.reduce((acc, d) => acc + (d.volume || 0), 0) /
    visibleData.length;

  // Calculate average energy (kWh)
  const avgEnergy =
    visibleData.reduce((acc, d) => acc + (d.energy || 0), 0) /
    visibleData.length;

  return [
    {
      value: avgVolume,
      label: `${avgVolume.toFixed(1)}`,
      color: '#90caf9',
      dashStyle: 'ShortDash',
      unit: 'm³',
    },
    {
      value: avgEnergy,
      label: `${avgEnergy.toFixed(1)}`,
      color: '#ffb74d',
      dashStyle: 'ShortDash',
      unit: 'kWh',
    },
  ];
};

/**
 * Chart Data Preparation Functions
 */

/**
 * @typedef {{
 *   name: string,
 *   data: number[][],
 *   color: string,
 *   type?: string,
 *   yAxis?: number,
 *   unit?: string,
 * }} ChartSeries
 */

/**
 * Prepares data for the consumption chart
 * @param {Object} basicEnergyData - Data from API
 * @param {Array} pairs - Unit pairs
 * @param {{ start?: string | null, end?: string | null }} dateRange - visible date range
 * @param {string} aggregation - 'day', 'week', or 'month'
 * @param {string} [efficiencyColor] - Color for efficiency line (optional)
 * @returns {{
 *   columnSeries: ChartSeries[],
 *   lineSeries: ChartSeries[],
 *   visibleData: Array<{ timestamp: string, volume: number, energy: number }>,
 * }} Chart data with series and bands
 */
export const prepareConsumptionChartData = (
  basicEnergyData,
  pairs,
  dateRange,
  aggregation,
  efficiencyColor = '#263238',
) => {
  if (!basicEnergyData || !basicEnergyData.AggregatedData) {
    return {
      columnSeries: [],
      lineSeries: [],
      visibleData: [],
    };
  }

  const sorted = [...basicEnergyData.AggregatedData].sort(
    (a, b) =>
      DateTime.fromISO(a.timestamp).toMillis() -
      DateTime.fromISO(b.timestamp).toMillis(),
  );

  // Filter to visible date range if specified
  let visibleData = sorted;
  if (dateRange && dateRange.start && dateRange.end) {
    const startMillis = DateTime.fromISO(dateRange.start).toMillis();
    const endMillis = DateTime.fromISO(dateRange.end).toMillis();

    visibleData = sorted.filter((d) => {
      const pointMillis = DateTime.fromISO(d.timestamp).toMillis();
      return pointMillis >= startMillis && pointMillis <= endMillis;
    });
  }

  // Prepare column series for volume and energy
  const volumeSeries = {
    name: 'm³',
    data: visibleData.map((d) => [
      DateTime.fromISO(d.timestamp).toMillis(),
      d.volume || 0,
    ]),
    color: '#90caf9',
  };

  const energySeries = {
    name: 'kWh',
    data: visibleData.map((d) => [
      DateTime.fromISO(d.timestamp).toMillis(),
      d.energy || 0,
    ]),
    color: '#ffa726',
  };

  // Calculate efficiency for line series
  const efficiencySeries = {
    name: 'Virkningsgrad %',
    data: visibleData.map((d) => {
      const efficiency = calculateEfficiency(d.volume || 0, d.energy || 0);
      return [DateTime.fromISO(d.timestamp).toMillis(), efficiency];
    }),
    color: efficiencyColor,
    yAxis: 1,
  };

  return {
    columnSeries: [volumeSeries, energySeries],
    lineSeries: [efficiencySeries],
    visibleData: visibleData.map((d) => ({
      timestamp: d.timestamp,
      volume: d.volume || 0,
      energy: d.energy || 0,
    })),
  };
};

/**
 * Prepares data for the pattern chart (flow + power)
 * @param {Object} basicEnergyData - Data from API
 * @returns {ChartSeries[]} Array of series configurations
 */
export const preparePatternChartData = (basicEnergyData) => {
  if (!basicEnergyData || !basicEnergyData.HourData) {
    return [];
  }

  const sorted = [...basicEnergyData.HourData].sort(
    (a, b) =>
      DateTime.fromISO(a.timestamp).toMillis() -
      DateTime.fromISO(b.timestamp).toMillis(),
  );

  // Flow series (area chart)
  const flowSeries = {
    name: 'Flow',
    data: sorted.map((d) => [
      DateTime.fromISO(d.timestamp).toMillis(),
      d.volume || 0,
    ]),
    type: 'area',
    color: '#42a5f5',
    yAxis: 0,
    unit: 'm³',
  };

  return [flowSeries];
};
